import { Plus, Edit, Trash, X, RefreshCw, Package, AlertCircle, Check } from "lucide-react";
import { useEffect, useState } from "react";
import {
  AdminGetAllAddonPlans,
  AdminCreateAddonPlan,
  AdminUpdateAddonPlan,
  AdminDeleteAddonPlan,
  AdminToggleAddonPlanStatus
} from "../components/Api.jsx";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@radix-ui/react-dialog";
import Pagination from "../components/Pagination.jsx";

export default function AddonPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isManualId, setIsManualId] = useState(false);
  // const [refreshing, setRefreshing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [form, setForm] = useState({
    planName: "",
    description: "",
    price: "",
    durationDays: "",
    category: "",
    difficultyLevel: "",
    appStoreProductId: "",
  });

  useEffect(() => {
    fetchAll();
  }, []);

  // Auto-generate App Store Product ID
  useEffect(() => {
    if (!isManualId || !form.appStoreProductId) {
      const name = form.planName.toLowerCase().replace(/\s+/g, '_');
      if (name) {
        setForm(prev => ({ ...prev, appStoreProductId: `gym_${name}_addon` }));
        setIsManualId(false); // Reset manual flag if we are auto-generating because it was empty
      }
    }
  }, [form.planName, isManualId]);

  const fetchAll = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await AdminGetAllAddonPlans();
      console.log('API Response:', response);
      console.log('Response data:', response?.data);

      // The API returns: { message: "...", data: [...], status: 200, timestamp: "..." }
      // So we need to access response.data.data
      const plansData = response?.data?.data;
      console.log('Plans data:', plansData);

      setPlans(Array.isArray(plansData) ? plansData : []);
    } catch (error) {
      console.error('Error fetching addon plans:', error);
      setPlans([]);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchAll(true);
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.planName?.trim()) newErrors.planName = 'Plan Name is required';
    if (!form.durationDays) newErrors.durationDays = 'Duration is required';
    else if (isNaN(form.durationDays) || Number(form.durationDays) <= 0) newErrors.durationDays = 'Duration must be valid';

    if (!form.category?.trim()) newErrors.category = 'Category is required';
    if (!form.price) newErrors.price = 'Price is required';
    else if (isNaN(form.price) || Number(form.price) <= 0) newErrors.price = 'Price must be valid';

    if (!form.appStoreProductId?.trim()) newErrors.appStoreProductId = 'App Store Product ID is required';
    else if (!form.appStoreProductId.startsWith('gym_')) newErrors.appStoreProductId = 'Must start with gym_';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setForm({ planName: "", description: "", price: 0, durationDays: 30, category: "", difficultyLevel: "BEGINNER", appStoreProductId: "" });
    setEditingId(null);
    setShowForm(false);
    setIsManualId(false);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      // Validate required fields
      if (!form.planName.trim()) {
        alert('Plan name is required');
        return;
      }
      if (!form.category.trim()) {
        alert('Category is required');
        return;
      }
      const durationDaysValue = Number(form.durationDays);
      if (!form.durationDays || form.durationDays === '' || durationDaysValue <= 0 || isNaN(durationDaysValue)) {
        alert('Duration days must be a positive number. Current value: ' + form.durationDays);
        return;
      }

      const payload = {
        planName: form.planName.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        durationDays: durationDaysValue,
        category: form.category.trim(),
        difficultyLevel: form.difficultyLevel,
        appStoreProductId: form.appStoreProductId.trim(),
        currency: "INR"
      };

      // Debug logging
      console.log('Form data:', form);
      console.log('durationDays value:', form.durationDays);
      console.log('durationDays type:', typeof form.durationDays);
      console.log('Number(form.durationDays):', Number(form.durationDays));
      console.log('Payload being sent:', payload);

      // Additional validation for payload
      if (!payload.durationDays || payload.durationDays <= 0) {
        alert('Duration days is invalid in payload: ' + payload.durationDays);
        return;
      }

      if (editingId) {
        console.log('Updating addon plan with ID:', editingId);
        await AdminUpdateAddonPlan(editingId, payload);
        showToast('Plan updated successfully');
      } else {
        console.log('Creating new addon plan');
        await AdminCreateAddonPlan(payload);
        showToast('Plan created successfully');
      }
      await fetchAll();
      resetForm();
    } catch (error) {
      console.error('Error creating/updating addon plan:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);

      if (error.response?.data?.message) {
        showToast('Error: ' + error.response.data.message, 'error');
      } else if (error.message) {
        showToast('Error: ' + error.message, 'error');
      } else {
        showToast('Unknown error occurred', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingId(plan.id);
    setShowForm(true);
    setForm({
      planName: plan.planName || "",
      description: plan.description || "",
      price: plan.price ?? 0,
      durationDays: plan.durationDays ?? 30,
      category: plan.category || "",
      difficultyLevel: plan.difficultyLevel || "BEGINNER",
      appStoreProductId: plan.appStoreProductId || "",
    });

    // Smart Auto-sync: Check if existing ID matches the auto-generated pattern
    const expectedId = `gym_${(plan.planName || "").toLowerCase().replace(/\s+/g, '_')}_addon`;
    if (plan.appStoreProductId && plan.appStoreProductId !== expectedId) {
      setIsManualId(true); // Custom ID, protect it
    } else {
      setIsManualId(false); // standard or empty ID, allow sync
    }
  };

  const handleDelete = async (plan) => {
    await AdminDeleteAddonPlan(plan.id);
    await fetchAll();
  };

  const handleToggleStatus = async (plan) => {
    await AdminToggleAddonPlanStatus(plan.id, !plan.isActive);
    await fetchAll();
  };

  return (
    <div className="min-h-screen bg-white text-black p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {toast.message}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-emerald-100 p-3 rounded-lg">
                  <Package className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Create and manage addon plans that allow users to purchase additional activities for a limited duration. Each addon plan is classified based on difficulty level to match user preferences and fitness goals.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh addon plans"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogTrigger asChild>
                  <button
                    onClick={() => setShowForm(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    aria-label="Create new addon plan"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Add Plan</span>
                  </button>
                </DialogTrigger>
                <DialogContent className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0">
                  <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 relative">
                    <div className="flex justify-between items-center mb-4">
                      <DialogTitle className="text-xl font-bold">
                        {editingId ? 'Edit Addon Plan' : 'Add Addon Plan'}
                      </DialogTitle>
                      <button
                        onClick={resetForm}
                        className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div>
                        <label className="block text-sm mb-1">Plan Name *</label>
                        <input
                          value={form.planName}
                          onChange={(e) => {
                            setForm({ ...form, planName: e.target.value });
                            if (errors.planName) setErrors({ ...errors, planName: null });
                          }}
                          className={`w-full p-2 rounded border text-sm ${errors.planName ? 'border-red-500' : ''}`}
                        />
                        {errors.planName && <p className="text-red-500 text-xs mt-1">{errors.planName}</p>}
                      </div>

                      <div>
                        <label className="block text-sm mb-1">App Store Product ID * (Convention: gym_[addon_name]_addon)</label>
                        <input
                          value={form.appStoreProductId}
                          placeholder="e.g., gym_nutrition_addon"
                          onChange={(e) => {
                            setForm({ ...form, appStoreProductId: e.target.value });
                            setIsManualId(true);
                            if (errors.appStoreProductId) setErrors({ ...errors, appStoreProductId: null });
                          }}
                          className={`w-full p-2 rounded border text-sm ${errors.appStoreProductId ? 'border-red-500' : ''}`}
                        />
                        {errors.appStoreProductId && <p className="text-red-500 text-xs mt-1">{errors.appStoreProductId}</p>}
                      </div>

                      {/* Compact row for Duration, Category, Difficulty, and Price */}
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="block text-xs mb-1">Duration (days) *</label>
                          <input
                            type="number"
                            value={form.durationDays}
                            onChange={(e) => {
                              setForm({ ...form, durationDays: e.target.value });
                              if (errors.durationDays) setErrors({ ...errors, durationDays: null });
                            }}
                            className={`w-full p-2 rounded border text-sm ${errors.durationDays ? 'border-red-500' : ''}`}
                            min={1}
                          />
                          {errors.durationDays && <p className="text-red-500 text-xs mt-1">{errors.durationDays}</p>}
                        </div>
                        <div>
                          <label className="block text-xs mb-1">Category *</label>
                          <input
                            value={form.category}
                            onChange={(e) => {
                              setForm({ ...form, category: e.target.value });
                              if (errors.category) setErrors({ ...errors, category: null });
                            }}
                            className={`w-full p-2 rounded border text-sm ${errors.category ? 'border-red-500' : ''}`}
                          />
                          {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
                        </div>
                        <div>
                          <label className="block text-xs mb-1">Difficulty</label>
                          <select
                            value={form.difficultyLevel}
                            onChange={(e) => setForm({ ...form, difficultyLevel: e.target.value })}
                            className="w-full p-2 rounded border text-sm"
                          >
                            <option value="" disabled hidden>Select Difficulty</option>
                            <option value="BEGINNER">BEGINNER</option>
                            <option value="INTERMEDIATE">INTERMEDIATE</option>
                            <option value="ADVANCED">ADVANCED</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs mb-1">Price (INR) *</label>
                          <input
                            type="number"
                            value={form.price}
                            onChange={(e) => {
                              setForm({ ...form, price: e.target.value });
                              if (errors.price) setErrors({ ...errors, price: null });
                            }}
                            className={`w-full p-2 rounded border text-sm ${errors.price ? 'border-red-500' : ''}`}
                            min={0}
                            step="0.01"
                          />
                          {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm mb-1">Description</label>
                        <textarea
                          value={form.description}
                          onChange={(e) => setForm({ ...form, description: e.target.value })}
                          className="w-full p-2 rounded border text-sm"
                          rows={3}
                        />
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button
                          type="submit"
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm "
                          disabled={loading}
                        >
                          <Plus className="w-4 h-4" /> {editingId ? 'Update Plan' : 'Create Plan'}
                        </button>
                        <button
                          type="button"
                          className="px-3 py-2 rounded-lg border text-sm"
                          onClick={resetForm}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* Plans List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && plans.length === 0 && (
          <div>Loading plans...</div>
        )}
        {!loading && plans.length === 0 && (
          <div className="col-span-full text-center py-8">
            <p className="text-gray-500">No addon plans found</p>
          </div>
        )}
        {(Array.isArray(plans) ? plans : []).slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((plan, idx) => (
          <div key={plan.id + '-' + idx} className="bg-white rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-blue-300">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold">{plan.planName}</h3>
                <p className="text-2xl font-bold text-indigo-600 mt-1">₹ {plan.price}</p>
                <p className="text-sm text-gray-700 mt-1">Duration: {plan.durationDays} days</p>
                <p className="text-sm text-gray-700">Category: {plan.category || '-'}</p>
                <p className="text-sm text-gray-700">Difficulty: {plan.difficultyLevel}</p>
                <p className="text-sm text-gray-700">Active: {plan.isActive ? 'Yes' : 'No'}</p>
                {plan.description && (
                  <p className="text-sm text-gray-700 mt-2 line-clamp-3">{plan.description}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  onClick={() => handleEdit(plan)}>
                  <Edit className="w-4 h-4" />
                </button>
                {/* <button className="p-2 rounded bg-red-100 text-red-600 hover:scale-110 transition-transform" onClick={()=>handleDelete(plan)}>
                  <Trash className="w-4 h-4" />
                </button> */}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button className="px-3 py-1 rounded bg-indigo-600 text-white text-sm" onClick={() => handleToggleStatus(plan)}>
                {plan.isActive ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>

      <Pagination 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil((Array.isArray((Array.isArray(plans) ? plans : [])) ? (Array.isArray(plans) ? plans : []).length : ((Array.isArray(plans) ? plans : [])?.length || 0)) / itemsPerPage)}
          totalElements={(Array.isArray((Array.isArray(plans) ? plans : [])) ? (Array.isArray(plans) ? plans : []).length : ((Array.isArray(plans) ? plans : [])?.length || 0))}
          pageSize={itemsPerPage}
          setPageSize={setItemsPerPage}
        />
    </div>
  );
}